import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { COMMUNICATION_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${COMMUNICATION_SERVICE_BASE_URL}/api/v1/message-templates`;

export default function useMessageTemplates(page = 0, pageSize = 50) {
  const authToken = sessionStorage.getItem("authToken");
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  const fetchAllMessageTemplates = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await apiClient.get(
        `${BASE_URL}?page=${page}&size=${pageSize}`
      );
      setMessageTemplates(res.data.content || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalElements(res.data.totalElements || 0);
    } catch (error) {
      handleAxiosErrors(error, "fetch all templates");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [authToken, page, pageSize]);

  const searchTemplates = useCallback(
    async (query = "", code = "ALL", searchPage = 0, size = pageSize) => {
      if (!authToken) return;

      const trimmed = String(query || "").trim();
      const hasQuery = trimmed.length >= 3;
      const hasCode = code && code !== "ALL";

      if (!hasQuery && !hasCode) {
        await fetchAllMessageTemplates();
        return;
      }

      setLoading(true);
      try {
        const params = { page: searchPage, size };
        if (hasQuery) params.q = trimmed;
        if (hasCode) params.code = code;

        const res = await apiClient.get(`${BASE_URL}/search`, { params });
        setMessageTemplates(res.data.content || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalElements(res.data.totalElements || 0);
      } catch (error) {
        handleAxiosErrors(error, "search message templates");
      } finally {
        setLoading(false);
      }
    },
    [authToken, fetchAllMessageTemplates, pageSize]
  );

  const fetchTemplateById = useCallback(
    async (templateId) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.get(`${BASE_URL}/${templateId}`);
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch template by id");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  const createTemplate = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(BASE_URL, payload);
        setMessageTemplates((prev) => [res.data, ...prev]);
        toast.success("Template created successfully");
      } catch (error) {
        handleAxiosErrors(error, "create template");
        throw error;
      }
    },
    [authToken]
  );

  const updateTemplate = useCallback(
    async (payload, templateId) => {
      if (!authToken) return;
      try {
        const res = await apiClient.put(`${BASE_URL}/${templateId}`, payload);
        setMessageTemplates((prev) =>
          prev.map((template) => (template.id === templateId ? res.data : template))
        );
        toast.success("Template updated successfully");
      } catch (error) {
        handleAxiosErrors(error, "update template");
        throw error;
      }
    },
    [authToken]
  );

  const patchTemplate = useCallback(
    async (patches, templateId) => {
      if (!authToken || patches.length === 0) return;
      try {
        const res = await apiClient.patch(`${BASE_URL}/${templateId}`, patches);
        setMessageTemplates((prev) =>
          prev.map((t) => (t.id === templateId ? res.data : t))
        );
        toast.success("Template updated successfully");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "update template");
        throw error;
      }
    },
    [authToken]
  );

  const deleteTemplate = useCallback(
    async (templateId) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${BASE_URL}/${templateId}`);
        setMessageTemplates((prev) =>
          prev.filter((template) => template.id !== templateId)
        );
        toast.success("Template deleted successfully");
      } catch (error) {
        handleAxiosErrors(error, "delete template");
      }
    },
    [authToken]
  );

  useEffect(() => {
    if (authToken) fetchAllMessageTemplates();
  }, [authToken, fetchAllMessageTemplates]);

  return {
    messageTemplates,
    loading,
    initialLoad,
    fetchAllMessageTemplates,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    patchTemplate,
    deleteTemplate,
    searchTemplates,
    totalPages,
    totalElements,
  };
}
